import { useEffect, useState } from 'react';
import api from '../services/api';
import { useAuthStore } from '../store/authStore';

// Session-scoped cache so multiple ScaleGate/consumer components mounting
// on the same page don't each fire GET /license/check. Cleared on window
// reload; the caller can bust it explicitly via `refetch()` (added below)
// after a subscription change.
let _cachedLicensePromise = null;
let _cachedLicenseValue = null;
let _cachedLicenseKey = null;
// Mounted hooks, so a refresh reaches every page already showing a gate.
const _listeners = new Set();
// Bumped by every refresh. A response from an earlier generation is stale --
// it may predate a purchase -- and must never overwrite a newer answer.
let _generation = 0;

// Drop the cached licence and refetch it everywhere. Call after anything that
// changes what the tenant may use, e.g. a payment for a plan or add-on applied.
export const invalidateLicense = () => {
  _generation += 1;
  _cachedLicensePromise = null;
  _cachedLicenseValue = null;
  _cachedLicenseKey = null;
  _listeners.forEach((listener) => listener());
};

// Resolves to { value, generation, failed }. Callers apply it only while
// `generation` is still current.
const fetchLicense = (cacheKey) => {
  if (_cachedLicensePromise && _cachedLicenseKey === cacheKey) return _cachedLicensePromise;
  _cachedLicensePromise = null;
  _cachedLicenseValue = null;
  _cachedLicenseKey = cacheKey;
  const generation = _generation;
  // Write through only if neither the key (a tenant switch mid-flight) nor
  // the generation (a refresh mid-flight) has moved on.
  const current = () => _cachedLicenseKey === cacheKey && _generation === generation;
  const promise = api.get('/license/check')
    .then((res) => {
      const value = res.data || null;
      if (current()) _cachedLicenseValue = value;
      return { value, generation, failed: false };
    })
    .catch(() => {
      if (current()) _cachedLicensePromise = null;
      return { value: null, generation, failed: true };
    });
  _cachedLicensePromise = promise;
  return promise;
};

// Returns { license, tier, isScale, isGrowthOrScale, hasFeature, isLoading, refetch }.
// `tier` is the string ("starter" | "growth" | "scale") — same
// values the backend uses in constants.py SubscriptionTiers. Callers that
// only want "is this Scale?" should use `isScale` directly.
export const useLicense = () => {
  const user = useAuthStore((state) => state.user);
  const cacheKey = `${user?.tenant_id || 'no-tenant'}:${user?.id || user?.email || 'anonymous'}`;
  const initialLicense = _cachedLicenseKey === cacheKey ? _cachedLicenseValue : null;
  // The licence is held with the account it belongs to, and read only for that
  // account: after an account switch, the previous account's licence is gone
  // at once, even before (or if) the new account's request succeeds.
  const [held, setHeld] = useState({ key: cacheKey, value: initialLicense });
  const license = held.key === cacheKey ? held.value : initialLicense;
  const [isLoading, setIsLoading] = useState(initialLicense === null);

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      fetchLicense(cacheKey).then(({ value, generation, failed }) => {
        // A stale answer is dropped: the newer request will set the state.
        if (cancelled || generation !== _generation) return;
        // A failed refresh keeps the last known licence rather than locking,
        // but only this account's: another account's is never carried over.
        setHeld((prev) => {
          if (!failed) return { key: cacheKey, value };
          return prev.key === cacheKey ? prev : { key: cacheKey, value: null };
        });
        setIsLoading(false);
      });
    };
    setIsLoading(true);
    load();
    _listeners.add(load);
    return () => { cancelled = true; _listeners.delete(load); };
  }, [cacheKey]);

  const tier = (license?.subscription_tier || 'starter').toLowerCase();
  const isScale = tier === 'scale';
  const isGrowthOrScale = tier === 'growth' || tier === 'scale';
  // Server-resolved features: plan default OR a purchased add-on OR an override.
  const entitlements = license?.feature_entitlements || {};
  const hasFeature = (key) => Boolean(entitlements[key]);

  const refetch = invalidateLicense;

  return { license, tier, isScale, isGrowthOrScale, hasFeature, isLoading, refetch };
};

export default useLicense;
